import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { API } from "@/lib/assets";

export default function ProfilePage() {
  const { handle } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!handle) { setError(true); setLoading(false); return; }
    fetch(`${API}/profile/${handle}`)
      .then(r => r.json())
      .then(res => { if (res.ok) setData(res); else setError(true); })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [handle]);

  const fade = (d = 0) => ({ initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.6, delay: d } });

  if (loading) return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
      <div className="w-6 h-6 border-2 border-[#E6FF00] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6">
      <div className="text-center max-w-sm">
        <p className="text-5xl mb-4">🔍</p>
        <h1 className="font-anton text-2xl text-white uppercase mb-2">Player not found</h1>
        <p className="font-body text-sm text-white/50 mb-8">This handle doesn't exist on PINWIN.</p>
        <Link to="/" className="inline-block font-mono-pw text-sm tracking-widest text-black bg-[#E6FF00] px-8 py-3 uppercase">Go to PINWIN</Link>
      </div>
    </div>
  );

  const initial = data.display_name?.charAt(0)?.toUpperCase() || "?";
  const wr = data.total_wins + data.total_losses > 0 ? Math.round((data.total_wins / (data.total_wins + data.total_losses)) * 100) : 0;

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex flex-col items-center px-6 py-16">
      <motion.div {...fade(0)} className="text-center max-w-md w-full">
        <motion.div {...fade(0.1)} className="flex justify-center mb-6">
          {data.avatar_url
            ? <img src={data.avatar_url} alt="" className="w-24 h-24 rounded-xl object-cover" />
            : <div className="w-24 h-24 rounded-xl bg-[#E6FF00] flex items-center justify-center"><span className="font-anton text-4xl text-black">{initial}</span></div>}
        </motion.div>
        <motion.h1 {...fade(0.2)} className="font-anton text-3xl md:text-4xl text-white uppercase mb-1">@{data.handle}</motion.h1>
        {data.display_name && data.display_name !== data.handle && <motion.p {...fade(0.25)} className="font-body text-sm text-white/50 mb-1">{data.display_name}</motion.p>}
        <motion.p {...fade(0.3)} className="font-mono-pw text-xs text-white/30 mb-6">
          JOINED {new Date(data.created_at).toLocaleDateString("en-US", { month: "short", year: "numeric" }).toUpperCase()}
        </motion.p>
        {data.bio && <motion.p {...fade(0.35)} className="font-body text-sm text-white/60 mb-6">{data.bio}</motion.p>}

        <motion.div {...fade(0.4)} className="flex gap-3 justify-center mb-4">
          {[{ n: data.total_wins, l: "WINS", c: "text-[#E6FF00]" }, { n: data.current_streak, l: "STREAK", c: "text-white" }, { n: data.squad_count, l: "SQUADS", c: "text-white" }].map((s, i) => (
            <div key={i} className="border border-white/10 px-5 py-3 text-center flex-1">
              <p className={`font-anton text-2xl ${s.c}`}>{s.n}</p>
              <p className="font-mono-pw text-[10px] text-white/40 tracking-widest uppercase mt-1">{s.l}</p>
            </div>
          ))}
        </motion.div>

        <motion.div {...fade(0.45)} className="mb-8">
          <div className="flex justify-between mb-1">
            <span className="font-mono-pw text-[10px] text-white/40 tracking-widest uppercase">All time</span>
            <span className="font-anton text-sm text-white">{wr}% W</span>
          </div>
          <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
            <div className="h-full bg-[#E6FF00] rounded-full transition-all duration-700" style={{ width: `${wr}%` }} />
          </div>
        </motion.div>

        {data.trophies?.length > 0 && (
          <motion.div {...fade(0.5)} className="mb-8">
            <p className="font-mono-pw text-[10px] text-white/40 tracking-widest uppercase mb-3">Trophies</p>
            <div className="flex flex-wrap gap-2 justify-center">
              {data.trophies.map(tr => <div key={tr.id} className="border border-[#E6FF00]/40 bg-[#E6FF00]/5 px-3 py-2" title={tr.name}><span className="text-xl">{tr.emoji}</span></div>)}
            </div>
          </motion.div>
        )}

        {data.collections?.length > 0 && (
          <motion.div {...fade(0.55)} className="mb-8">
            <p className="font-mono-pw text-[10px] text-white/40 tracking-widest uppercase mb-3">Collections</p>
            <div className="flex flex-wrap gap-2 justify-center">
              {data.collections.map(col => (
                <div key={col.id} className="border border-white/10 px-4 py-3 text-center min-w-[100px]">
                  <p className="text-2xl mb-1">{col.emoji}</p>
                  <p className="font-body text-xs text-white">{col.name}</p>
                  <p className="font-mono-pw text-[10px] text-white/30 mt-1">{col.item_count} items</p>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {data.squads?.length > 0 && (
          <motion.div {...fade(0.58)} className="mb-10">
            <p className="font-mono-pw text-[10px] text-white/40 tracking-widest uppercase mb-3">Active Squads</p>
            <div className="flex flex-col gap-2">
              {data.squads.map(sq => (
                <div key={sq.id} className="border border-white/10 px-4 py-3 flex justify-between items-center">
                  <span className="font-body text-sm text-white">{sq.name}</span>
                  <span className="font-anton text-sm text-[#E6FF00]">#{sq.position}</span>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        <motion.div {...fade(0.6)}>
          <a href="https://pinwin.social" className="block font-mono-pw text-sm tracking-widest text-black bg-[#E6FF00] px-8 py-4 uppercase text-center hover:brightness-110 transition-all">Get PINWIN</a>
        </motion.div>
      </motion.div>
    </div>
  );
}
